import { generateCareers, type Career } from "@/data/careers";
import type { TraitScores } from "@/lib/scoring";

/**
 * Neutral baseline so SEO career pages show stable, non-personalised content.
 * Real match percentages are computed per user inside the assessment flow.
 */
const NEUTRAL_TRAITS: TraitScores = {
  people: 50,
  creativity: 50,
  income: 50,
  freedom: 50,
  structure: 50,
} as TraitScores;

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

let cachedAll: Career[] | null = null;
let cachedBySlug: Map<string, Career> | null = null;

// Generated career pages loaded from the database (AI-built via the affiliate
// New career ideas flow). Merged into the in-memory lookups once loaded.
const generatedBySlug: Map<string, Career> = new Map();
let generatedLoaded = false;
let generatedLoadPromise: Promise<void> | null = null;

function invalidateCaches() {
  cachedAll = null;
  cachedBySlug = null;
}

export function isGeneratedCareersLoaded(): boolean {
  return generatedLoaded;
}

/** Fetch DB-backed generated career pages and merge into the slug cache. */
export function ensureGeneratedCareersLoaded(): Promise<void> {
  if (generatedLoadPromise) return generatedLoadPromise;
  generatedLoadPromise = (async () => {
    try {
      if (typeof window === "undefined") return;
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase
        .from("generated_careers" as any)
        .select("slug, payload");
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[careerSlug] failed to load generated_careers", error.message);
        return;
      }
      for (const row of ((data ?? []) as unknown) as Array<{ slug: string | null; payload: Career }>) {
        const c = row?.payload;
        if (!c?.name) continue;
        // Index by the DB slug (authoritative - this is what links/admin use)
        // AND by slugify(name) as a fallback, so the page resolves either way
        // when the AI-generated name differs from the queued category slug
        // (e.g. slug="skill-development" but name="Skill Development Coach").
        const nameSlug = slugify(c.name);
        if (row.slug) generatedBySlug.set(row.slug, c);
        if (nameSlug && !generatedBySlug.has(nameSlug)) generatedBySlug.set(nameSlug, c);
      }
      if (generatedBySlug.size > 0) invalidateCaches();

    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[careerSlug] generated_careers load threw", e);
    } finally {
      generatedLoaded = true;
    }
  })();
  return generatedLoadPromise;
}

// Kick off the fetch on first import in the browser so most navigations
// already have generated careers available without an explicit await.
if (typeof window !== "undefined") {
  ensureGeneratedCareersLoaded();
}

/** All careers with neutral baseline scores. Stable order: by name. */
export function getAllCareersForSEO(): Career[] {
  if (cachedAll) return cachedAll;
  const seedList = generateCareers(NEUTRAL_TRAITS);
  const seedSlugs = new Set(seedList.map((c) => slugify(c.name)));
  const extras: Career[] = [];
  generatedBySlug.forEach((c, slug) => {
    if (!seedSlugs.has(slug)) extras.push(c);
  });
  const list = [...seedList, ...extras].sort((a, b) => a.name.localeCompare(b.name));

  // Dev-only guard: catch duplicate slugs early.
  if (import.meta.env?.DEV) {
    const seen = new Map<string, string>();
    for (const c of list) {
      const s = slugify(c.name);
      if (seen.has(s)) {
        // eslint-disable-next-line no-console
        console.error(
          `[careerSlug] Duplicate slug "${s}" from "${c.name}" and "${seen.get(s)}".`,
        );
      } else {
        seen.set(s, c.name);
      }
    }
  }

  cachedAll = list;
  return list;
}

export function getCareerBySlug(slug: string): Career | undefined {
  if (!cachedBySlug) {
    cachedBySlug = new Map();
    for (const c of getAllCareersForSEO()) {
      cachedBySlug.set(slugify(c.name), c);
    }
  }
  return cachedBySlug.get(slug) ?? generatedBySlug.get(slug);
}

export function getRelatedCareers(career: Career, limit = 5): Career[] {
  const all = getAllCareersForSEO();
  return all
    .filter((c) => c.id !== career.id && c.category === career.category)
    .slice(0, limit);
}

export function careerPath(career: Career): string {
  return `/careers/${slugify(career.name)}`;
}

/**
 * Resolve a list of career names to Career objects, dropping any that
 * don't match an existing career page. Matching is case-insensitive and
 * also tolerates slug-equivalence so authoring is forgiving.
 */
export function resolveCareerNames(names: string[] | undefined): Career[] {
  if (!names || names.length === 0) return [];
  const all = getAllCareersForSEO();
  const byName = new Map<string, Career>();
  const bySlug = new Map<string, Career>();
  for (const c of all) {
    byName.set(c.name.toLowerCase(), c);
    bySlug.set(slugify(c.name), c);
  }
  const out: Career[] = [];
  const seen = new Set<Career["id"]>();
  for (const n of names) {
    const c = byName.get(n.toLowerCase()) ?? bySlug.get(slugify(n));
    if (c && !seen.has(c.id)) {
      seen.add(c.id);
      out.push(c);
    }
  }
  return out;
}
